import React, { useState, useEffect, useRef } from 'react';
import useScrollAnimation from '../hooks/useScrollAnimation';

const AUTO_ZOOM_POSITIONS = [
  { x: 50, y: 50, scale: 1 },      // Start: Full view
  { x: 20, y: 20, scale: 2 },      // Top-left corner
  { x: 80, y: 30, scale: 2.2 },    // Top-right
  { x: 70, y: 60, scale: 2 },      // Center-right
  { x: 30, y: 70, scale: 2.1 },    // Bottom-left
  { x: 50, y: 40, scale: 2.2 },    // Center-top
  { x: 50, y: 50, scale: 1 },      // Back to full view
];

const MakeupSpotlight = () => {
  const { ref: sectionRef, animationClass } = useScrollAnimation('scale-in', 0.1);
  const [imageTransform, setImageTransform] = useState({});
  const [isHovering, setIsHovering] = useState(false);
  const [autoZoomStep, setAutoZoomStep] = useState(0);
  const animationRef = useRef(null);

  useEffect(() => {
    if (!isHovering) {
      // Start auto-zoom animation
      const interval = setInterval(() => {
        setAutoZoomStep((prev) => (prev + 1) % AUTO_ZOOM_POSITIONS.length);
      }, 3000); // Change position every 3 seconds

      animationRef.current = interval;
      return () => clearInterval(interval);
    } else {
      // Clear animation when hovering
      if (animationRef.current) {
        clearInterval(animationRef.current);
      }
    }
  }, [isHovering]);

  // Apply auto-zoom transformation
  useEffect(() => {
    if (!isHovering) {
      const position = AUTO_ZOOM_POSITIONS[autoZoomStep];
      setImageTransform({
        image1: {
          transformOrigin: `${position.x}% ${position.y}%`,
          transform: `scale(${position.scale})`,
        }
      });
    }
  }, [autoZoomStep, isHovering]);

  const handleMouseMove = (e) => {
    if (!isHovering) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    setImageTransform({
      image1: {
        transformOrigin: `${x}% ${y}%`,
        transform: 'scale(2)',
      }
    });
  };

  const handleMouseEnter = () => {
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    setAutoZoomStep(0); // Reset to start
  };

  return (
    <section ref={sectionRef} className={`bg-[#FFF5F2] py-8 sm:py-12 md:py-16 lg:py-24 px-3 sm:px-4 md:px-6 ${animationClass}`}>
      <div className="max-w-screen-xl mx-auto">
        {/* Section Header - Responsive */}
        <div className="text-center mb-6 sm:mb-10 md:mb-12 lg:mb-16">
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl xl:text-6xl font-serif font-bold text-gray-900 leading-tight mb-3 sm:mb-4 md:mb-6">
            It's Not Just a Picture
            <br />
            <span className="bg-[#E85D44] text-[#FFD93D] px-3 sm:px-4 md:px-6 py-1 sm:py-1.5 md:py-2 inline-block text-2xl sm:text-3xl md:text-4xl lg:text-6xl xl:text-7xl">It's REAL 3D!</span>
          </h2>
        </div>

        {/* Centered Image with Auto-Zoom - Responsive */}
        <div className="flex justify-center mb-8 sm:mb-12 md:mb-16">
          <div
            className="relative bg-white rounded-xl sm:rounded-2xl overflow-hidden shadow-xl sm:shadow-2xl group cursor-zoom-in w-full max-w-3xl"
            style={{ height: 'auto', aspectRatio: '16/10' }}
            onMouseMove={handleMouseMove}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <img
              src="/images/lithophane-closeup-1.jpg"
              alt="Close-up showing 3D texture"
              className="w-full h-full object-cover transition-all duration-[2000ms] ease-in-out"
              style={imageTransform.image1 || {}}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            {/* Magnifier icon hint */}
            <div className="absolute top-2 sm:top-4 right-2 sm:right-4 bg-white/90 rounded-full p-1.5 sm:p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
              <svg className="w-4 h-4 sm:w-6 sm:h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            {/* Hover instruction */}
            {!isHovering && (
              <div className="absolute bottom-2 sm:bottom-4 left-1/2 transform -translate-x-1/2 bg-black/70 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm">
                Hover to zoom manually
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default MakeupSpotlight;
